import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import styles from './RichCheckout.module.scss'

import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import GratuityHeader from '../components/GratuityHeader'
import QRCode from '../components/QRCode'
import Dialog from '../components/dialog/Dialog'
import ConfirmModalV2 from '../components/ConfirmModalV2'

import useDeepCompareEffect from 'use-deep-compare-effect'

import { VNPusher as VNPusherPrototype } from '../VNPusher/VNPusher'
import { getPusherKey, getPusherCluster } from '../VNPusher/Selectors'
import { isEmpty } from '../VNUtil/VNUtils'

import { RICH_CHECKOUT_TEXT_PROMPT, TEAM_LOGO_EXISTS } from '../constants'

import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal'
import useCloseFreeOrder from '../hooks/useCloseFreeOrder'

import { ReactComponent as MobilePhoneIcon } from '../assets/icons/MobilePhoneIcon.svg'
import { ReactComponent as MobileReceiptIcon } from '../assets/icons/MobileReceiptIcon.svg'

import { addPaymentToOrderInProgress, closePreAuthedRCOrder, removePayment } from '../actions/order'
import { saveRichCheckoutOrderRequested } from '../actions/richCheckout/order'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { clearCart } from '../actions/cart'

import { getOrderInProgress } from '../selectors/order'
import { getTotal, getCartItems, getTaxExemptData } from '../selectors/cart'

import { isTabbed } from '../utils/orderStates'
import { getIsPreAuthTab } from '../utils/orderUtils'
import { orderUuidFromOrderNumber } from '../utils/orderNumbers'
import { formatLocalItems, formatRemoteItems, generateURL } from '../utils/richCheckout'
import { nextPosOrderNumber } from '../utils/orderNumbers'
import { formatOrderTotals } from '../utils/orderTotalUtils'
import { centsToDollars } from '../utils/formatters'
import useSpinner from '../context/hooks/useSpinner'
import { getUseTaxClasses } from '../selectors/menus'
import { getDeviceSerial } from '../utils/deviceData'

export const RC_TIMEOUT_DURATION = 45_000

const RichCheckout = ({
  deviceId,
  clearOrder,
  orderDidComplete,
  removePaymentFromTab,
  goBack,
  networkAvailable,
  total,
  cartItems,
  className,
}) => {
  const classNames = cn(styles.richCheckout, className)
  const VNPusher = useRef()
  const timeoutRef = useRef()
  const dispatch = useDispatch()

  const pusherKey = useSelector(getPusherKey)
  const pusherCluster = useSelector(getPusherCluster)

  const [isScanned, setIsScanned] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [orderNumber] = useState(nextPosOrderNumber())
  const [orderId, setOrderId] = useState(orderUuidFromOrderNumber(orderNumber))
  const [promptHeader, setPromptHeader] = useState('Pay With App')
  const [promptMessage, setPromptMessage] = useState(RICH_CHECKOUT_TEXT_PROMPT)
  const [socketErrorsText, setSocketErrorsText] = useState(null)
  const [loadRemoteOrderTotal, { data, loading, success, failed }] = useRemoteOrderTotal()
  const { startSpinner, stopSpinner, showSpinner } = useSpinner()
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const tabbed = isTabbed(orderInProgress)
  const useTaxClasses = useSelector(getUseTaxClasses)
  const taxExempt = useSelector(getTaxExemptData)?.taxExempt || false

  const remoteOrderTotal = formatOrderTotals(data, useTaxClasses, taxExempt)
  const totalInCents = getTotal()
  let items = formatLocalItems(cartItems)

  if (success) {
    items = formatRemoteItems(remoteOrderTotal?.lineItems)
  }

  if (!isEmpty(orderInProgress)) {
    items = formatLocalItems(orderInProgress.itemModels)
  }

  const totalDisplay = tabbed ? centsToDollars(totalInCents) : success ? remoteOrderTotal?.totalDisplay : centsToDollars(total)
  const noPaymentRequired = totalInCents === 0
  const isMobileViewPort = window.matchMedia('(max-width: 500px)').matches
  const [qrCodeData, setQRCodeData] = useState()
  const [confirmWithoutSignal, setConfirmWithoutSignal] = useState(false)
  const { closeFreeOrder } = useCloseFreeOrder()
  const primaryText = loading ? 'Loading...' : totalDisplay

  const finishTransactionSubtext = 'Please finish this\n transaction on your phone.'

  const showMobileReceiptSubtext = isMobileViewPort
    ? 'Please show your mobile receipt to the attendant to confirm your order.'
    : 'Please show your mobile receipt to the\n attendant to confirm your order.'

  const onFailedSocketHandler = useCallback(
    (err) => {
      const displayErrorMessage = err?.error?.data?.message || err?.message || 'An unknown error occurred.'
      setSocketErrorsText(`Please manually confirm your order.\n${displayErrorMessage}`)
      // Don't immediately show confirmation when offline - let the RC_TIMEOUT_DURATION handle it
      if (networkAvailable) {
        setConfirmWithoutSignal(true)
      }
    },
    [networkAvailable],
  )

  useEffect(() => {
    VNPusher.current = new VNPusherPrototype(pusherKey, pusherCluster, onFailedSocketHandler)
    if (!tabbed) {
      loadRemoteOrderTotal()
    }

    if (!isEmpty(orderInProgress)) {
      setOrderId(orderInProgress.uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isSuccess) {
      clearTimeout(timeoutRef.current)
      return
    }

    // if user is offline or user has scanned and pusher is not responding with a success message
    if (!networkAvailable || isScanned) {
      timeoutRef.current = setTimeout(() => {
        setConfirmWithoutSignal(true)
      }, RC_TIMEOUT_DURATION)
    }

    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [isScanned, isSuccess, networkAvailable])

  // start spinner when qr code data is not available
  // stop spinner when qr code data is available
  useEffect(() => {
    if (!qrCodeData) {
      startSpinner()
      return
    }
    if (showSpinner) {
      stopSpinner()
    }
    return () => {
      stopSpinner()
    }
  }, [showSpinner, startSpinner, stopSpinner, qrCodeData])

  useEffect(() => {
    if (!isTabbed(orderInProgress)) return

    if (orderInProgress.payments?.length) {
      removePaymentFromTab({ order: orderInProgress })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isScanned) {
      setPromptHeader('Processing...')
      setPromptMessage('please wait')
    } else {
      setPromptHeader('Pay With App')
      setPromptMessage(RICH_CHECKOUT_TEXT_PROMPT)
    }
  }, [isScanned])

  useEffect(() => {
    if (success) {
      setOrderId(remoteOrderTotal.uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success])

  useEffect(() => {
    if (noPaymentRequired && !loading && (success || failed || tabbed)) {
      closeFreeOrder(orderId, tabbed)
    }
  }, [totalInCents, loading, success, failed, closeFreeOrder, noPaymentRequired, tabbed, orderId])

  useEffect(() => {
    if (isSuccess) {
      setPromptHeader('Success!')
      onConfirmOrder()
    } else {
      setPromptHeader('Pay with App')
      setPromptMessage(RICH_CHECKOUT_TEXT_PROMPT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  useDeepCompareEffect(() => {
    if (tabbed || (!loading && (success || failed))) {
      // tabbed order already has a orderNumber
      if (isTabbed(orderInProgress)) {
        setQRCodeData(generateURL(orderId, orderInProgress.orderNumber, deviceId, totalInCents, items))
      } else {
        setQRCodeData(generateURL(orderId, orderNumber, deviceId, totalInCents, items))
      }
    }
  }, [orderId, orderNumber, deviceId, totalInCents, items, loading, success, failed])

  const onBind = () => {
    setIsScanned(true)
  }

  function getTotal() {
    if (tabbed) {
      return orderInProgress?.amountInCents
    }

    return remoteOrderTotal?.totalAmountInCents ?? total
  }

  const onOrderCreated = () => {
    setIsSuccess(true)
  }

  const onConfirmOrder = () => {
    if (isTabbed(orderInProgress)) {
      const tenderAmountInCents = getTotal()
      const paymentType = 'mobile_app'
      const shortDescription = 'App'

      if (getIsPreAuthTab(orderInProgress)) {
        dispatch(closePreAuthedRCOrder({ uuid: orderId }))
        return
      }

      dispatch(addPaymentToOrderInProgress({ tenderAmountInCents, paymentType, shortDescription, isRichCheckout: true }))
    } else {
      dispatch(saveRichCheckoutOrderRequested({ uuid: orderId, orderNumber }))
    }

    clearOrder()
    orderDidComplete(orderId)
  }

  // pusher socket for rich checkout
  useEffect(() => {
    VNPusher.current.subscribeDeviceChannel()
    VNPusher.current.bindOrderTransfered(onBind)
    VNPusher.current.bindOrderCreated(onOrderCreated)

    // we'll want to use the below when the ios sdk persists the order uuid from the qr code
    // const orderChannel = socket.subscribe(`private-order-${uuid}`)
    // orderChannel.bind('order-created', (data) => {
    //   saveRichCheckoutOrder({ uuid, orderNumber })
    //   history.replace(`/rich-checkout/receipt/${uuid}`)
    // })
    return () => {
      VNPusher.current?.resetSocket()
    }
    // TODO: We need to audit the exhaustive dependencies here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={classNames}>
      <VNConcessionsNavBar onClick={goBack} textDisplay={isMobileViewPort && primaryText} showIcon={networkAvailable} />
      <GratuityHeader className={cn([styles.gratuityHeader, styles.hideOnMobile])} primaryText={primaryText} />
      <div className={styles.qrCodeContainer}>
        {!isScanned && qrCodeData && (
          <div className={styles.qrCode}>
            <QRCode data={qrCodeData} />
          </div>
        )}
      </div>
      <div className={styles.footer}>
        {TEAM_LOGO_EXISTS && <img alt="Team Logo" className={styles.teamLogo} src="/teamLogo.png" />}
        <p>{promptHeader}</p>
        <p>{promptMessage}</p>
      </div>
      {isScanned && !isSuccess && (
        <Dialog>
          <ConfirmModalV2
            headerText={<MobilePhoneIcon className={styles.icon} />}
            subtext={finishTransactionSubtext}
            buttonTwoText={'GO BACK'}
            onButtonTwoClick={goBack}
            singleButtonOnly={true}
          />
        </Dialog>
      )}
      {confirmWithoutSignal && (
        <Dialog>
          <ConfirmModalV2
            headerText={<MobileReceiptIcon className={styles.icon} />}
            subtext={socketErrorsText || showMobileReceiptSubtext}
            buttonOneText={'GO BACK'}
            onButtonOneClick={goBack}
            buttonTwoText={'CONFIRM ORDER'}
            onButtonTwoClick={onConfirmOrder}
          />
        </Dialog>
      )}
    </div>
  )
}

const mapStateToProps = (state) => {
  const deviceId = state?.config?.uuid ?? getDeviceSerial()
  const networkAvailable = state?.config?.networkAvailable ?? false
  const total = getTotal(state)
  const cartItems = getCartItems(state)

  return {
    deviceId,
    total,
    cartItems,
    networkAvailable,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? function () {}
  const replace = props?.history?.replace ?? function () {}

  return {
    goBack,
    clearOrder: () => {
      dispatch(clearCart())
      dispatch(clearPromotionsOnOrder())
    },
    orderDidComplete: (orderId) => {
      replace(`/receipt/${orderId}`)
    },
    removePaymentFromTab: (order) => dispatch(removePayment(order, 0, true)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RichCheckout)
